<template>
  <div
    ref="wrapper"
    class="editable-label clear-float notranslate"
    :class="{
      disabled: disabled,
      'editable-label--multiline': useTextarea || displayTiptapMenu,
      'editable-label--no-legend': legend === '',
      'generic-border--thin-border generic-border--focus-shadow': genericBorder,
    }"
    @click="disabled ? $emit('click') : edit($event)"
  >
    <div v-show="legend !== ''" class="legend v-centerer legend--bottom-margin">
      <span>{{ legend }}</span>
      <span class="normal"><slot name="legend" /></span>
      <div v-if="tiptapTasks !== ''" class="v-centerer tiptap-tasks ml10em normal">
        <Icon remix-icon="checkbox-line" />
        <span>{{ tiptapTasks }}</span>
      </div>
    </div>
    <textarea
      v-if="useTextarea"
      ref="area"
      v-resize-event="onResize"
      class="generic-border--thin-border generic-border--focus-shadow textarea"
      rows="1"
      :value="value"
      :disabled="disabled"
      :placeholder="placeholder"
      @input="textareaEdited"
      @blur="textareaBlur"
      @focus="edit(false)"
      @resize="onResize"
      @keyup="$emit('keyup', $event)"
    />
    <div v-else-if="useTiptapEditor">
      <TextField
        ref="textfield"
        :value="parsedValue"
        class="editable-label__text-field"
        :no-menu="!displayTiptapMenu"
        :disabled="disabled"
        :hide-undo-redo="hideUndoRedo"
        :edition-timeout="textareaEditionTimeout"
        :borderless="!displayTiptapBorder"
        :placeholder="placeholder"
        @input="$emit('input', $event instanceof Object ? JSON.stringify($event) : $event)"
        @focus="$emit('focus')"
        @blur="$emit('blur')"
      />

    </div>
    <div v-else>
      <div
        v-if="!ellipsize"
        v-show="!editing"
        class="editable-label-text"
        :class="customTextClasses"
      >
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div :placeholder="placeholder" v-html="sanitizedValue" />
      </div>
      <EllipsisDiv
        v-else
        v-show="!editing"
        class="editable-label-text"
        :class="customTextClasses"
        :text="sanitizedValue"
        :placeholder="placeholder"
      />
      <div
        ref="area"
        v-field-visible="editing"
        class="textarea"
        contenteditable="true"
        type="email"
        :placeholder="placeholder"
        @keydown="inputChanged($event)"
        @keyup="detectEnter($event)"
        @focus="focusing()"
        @input="bInput"
      />
    </div>
  </div>
</template>

<script>
/**
 * Field for problem sheet edition. Contains a legend label and an editable div.
 * Can be used as an autocomplete field if the 'autocomplete' param is set.
 *
 * @vue-prop {String} legend - Immutable string. DOM elt : clickable to edit the value
 * @vue-prop {String} v-model - text value. May also be an html string
 * @vue-prop {Array} autocomplete - array of items formatted as follow:
 *                                  [{id: 123, label:'Text to display as autocomplete'}]
 * @vue-prop {Number} autocompleteChipsType - Any of AutocompleteChipsTypes constants. The type of data of a chip
 * @vue-prop {String} placeholder - Text to display if the value is an empty string
 * @vue-prop {Boolean} focusOnMount - Focus the field to allow editing on mount
 * @vue-prop {Boolean} disabled - Make comp uneditable
 * @vue-prop {Boolean} ellipsize - If set and true, make comp behave as an <e-div>
 * @vue-prop {Boolean} isFsEditor - FullScreen editor. If on mobile / tablet with a soft keyboard,
 *                                  this editable-label is put at the top of the screen so it's always visible
 * @vue-prop {Boolean} keywords - If set and true, this field will be only filled with keywords from autocomplete
 *                                or user input
 * @vue-prop {Boolean} preventClickOutside - Prevent triggering the event handler when clicking outside the field
 *                     Used when animating the mobile editor EditableLabel
 * @vue-prop {Boolean} useTiptapEditor - Use a specific rich text editor for this field.
 *                                       The value of this field should then be JSON-stringified
 * @vue-prop {Boolean} [displayTiptapMenu] - If useTiptapEditor, display the menu of the editor
 * @vue-prop {Boolean} [displayTiptapTasks] - If useTiptapEditor, display the number of completed and uncompleted tasks
 * @vue-prop {Boolean} [displayTiptapBorder=true] - Set to false to remove the tiptap editor border.
 * @vue-prop {Boolean} displayTiptapMenu - If useTiptapEditor, display the menu of the editor
 * @vue-prop {Number} [textareaEditionTimeout=1000] - Default delay before emitting an input event when editing
 *                                                    the textarea
 * @vue-prop {Boolean} genericBorder - Adapt the design to Akiani's work
 *
 * @vue-event {} focus - Emitted when editable field receives focus
 * @vue-event {} startEdit - Emitted when edition begins
 * @vue-event {String} input - Emitted when the value changes
 * @vue-event {} textareaAutoSize - Emitted when the textarea elt is resized
 * @vue-event {KeyboardEvent} keyup - Forwarded from div[contenteditable]
 * @vue-event {} updated - Emitted when the virtual DOM is updated
 */

import $ from 'jquery';
// Components
import EllipsisDiv from '@/components/ellipsize.vue';
import Icon from '@/components/icon.vue';
import TextField from '@/components/text-field/text-field.vue';
import {
  decodeHTML,
  detectEscapeKey,
  getCaretInfo,
  getCharBeforeCaret,
  isMobile,
  parseStringToObject,
  preventPastingStyles,
  removeTrailingBreak,
  replaceRemovableWord,
  safeAutocompleteString,
  setCursorAtEnd,
  textareaAutoSize,
} from '@/utils/main';
import { AutocompleteChipsTypes } from '@/utils/classes/AutocompleteChipsTypes';
import { getTasksFromTiptapContent } from '@/plugins/tiptap';
import { ShizenGlobalEvent, globalEventDispatcher } from '@/utils/event-dispatcher/global';


const numCodes = Array.from({ length: 10 }, (v, k) => k + 47);
const charCodes = Array.from({ length: 26 }, (v, k) => k + 64);
const numpadCodes = Array.from({ length: 10 }, (v, k) => k + 95);

function autocompleteArea(evt, comp) {
  const code = evt.keyCode || 0;
  const ta = evt.target;
  const $ta = $(ta);
  const charEntered = (!code || Number(code) === 229) ? getCharBeforeCaret(ta) : evt.key;
  const caretInfo = getCaretInfo(ta);
  const { words } = caretInfo;
  const wLen = words.length;
  let lastWord = words[wLen - 1];

  // On non-mobile devices, this function is triggered on keydown i.e the character hasn't been added yet
  if (evt.type === 'keydown') {
    if (numCodes.includes(evt.keyCode) || charCodes.includes(evt.keyCode) || numpadCodes.includes(evt.keyCode)) {
      lastWord += evt.key;
    } else if (Number(evt.keyCode) === 8) {
      // Same for backspace event
      lastWord = lastWord.slice(0, -1);
    }
  }
  const $ac = $ta.autocomplete({
    delay: 0,
    source: comp.autocomplete,
    minLength: 1,
    select: (e, ui) => {
      replaceRemovableWord(ta, lastWord, ui.item.label, ui.item.id, comp.autocompleteChipsType);
      comp.preventNext = true;
      return false;
    },
    open(e) {
      comp.autocompleteOpened = true;
      const $input = $(e.target);
      const $results = $input.autocomplete('widget');
      const newTop = $results.position().top - $results.height() - $input.height();
      if (!comp.isFsEditor) {
        $results.css('top', `${newTop}px`);
      }
      setTimeout(() => {
        const $closeBtn = $('<img class="ui-autocomplete-close" src="/images/close.png" alt="" />');
        $ac.prepend($closeBtn);
        $closeBtn.on('click', (closeEvt) => {
          $ta.autocomplete('close');
          closeEvt.stopPropagation();
        });
      }, 0);
    },
    close() {
      comp.autocompleteOpened = false;
    },
    focus() {
      return false;
    },
  }).autocomplete('widget');
  // Trigger search manually
  setTimeout(() => {
    if (!comp.autocompleteOpened && !evt.noSearch) {
      $ta.autocomplete('search', lastWord);
    }
  }, 200);

  // If keywords field, and user types ',' or ';' or space, create a new keyword
  if (comp.keywords && !isMobile && (code === 188 || code === 59 || code === 190 || code === 32)) {
    if (lastWord !== '' && !lastWord.match(/^\s+$/)) {
      replaceRemovableWord(ta, lastWord);
    }
    if (evt.preventDefault) {
      evt.preventDefault();
    }
    return;
  }
  // Mobile device, use charEntered
  if (comp.keywords && isMobile && (charEntered === ';' || charEntered === ',' || charEntered.match(/\s/))) {
    if (charEntered.match(/\s/)) {
      if (words.length > 1 && !evt.noSearch) {
        lastWord = words[words.length - 2];
      }
    } else {
      lastWord = lastWord.slice(0, -1);
    }
    if (lastWord !== '' && !lastWord.match(/^\s+$/)) {
      replaceRemovableWord(ta, lastWord);
    }
  }
}

export default {

  name: 'EditableLabel',

  components: {
    EllipsisDiv,
    Icon,
    TextField,
  },

  props: {
    legend: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number],
      default: '',
    },
    autocomplete: {
      type: Array,
      default: () => [],
    },
    autocompleteChipsType: {
      type: Number,
      default: 1,
      validator: v => AutocompleteChipsTypes.ALL_TYPES.includes(v),
    },
    placeholder: {
      type: String,
      default: '',
    },
    focusOnMount: Boolean,
    disabled: Boolean,
    ellipsize: Boolean,
    isFsEditor: Boolean,
    keywords: Boolean,
    useTextarea: Boolean,
    useTiptapEditor: Boolean,
    displayTiptapMenu: Boolean,
    displayTiptapTasks: Boolean,
    displayTiptapBorder: {
      type: Boolean,
      default: true,
    },
    preventClickOutside: Boolean,
    hideUndoRedo: Boolean,
    textareaEditionTimeout: {
      type: Number,
      default: 1000,
    },
    genericBorder: Boolean,
    customTextClasses: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      editing: false,
      preventNext: false,
      autocompleteOpened: false,
      endEditOnClickEnd: false,
      textareaSaved: false,
      /** Codes that should not trigger autocomplete */
      noEventKeyCodes: [13, 37, 38, 39, 40],
      editTimeout: null,
    };
  },

  computed: {
    sanitizedValue() {
      return safeAutocompleteString(this.value);
    },
    /** Object|String: Value editable by the tiptap editor */
    parsedValue() {
      // NOTE Prior to v1.7.39, tiptap content was stored in the DB as a JSON object, hence the JSON parsing.
      // From 1.7.39 onwards, HTML is prefered as it makes it easier to work with in the back-end.
      // `parseStringToObject` should simply fail and return the HTML string when not using JSON.
      return parseStringToObject(this.useTiptapEditor && this.value !== null ? this.value : '');
    },
    /** String: When using tiptap editor, display the tasks (completed or not) next to the legend */
    tiptapTasks() {
      if (this.useTiptapEditor && this.displayTiptapTasks) {
        const tasks = getTasksFromTiptapContent(this.value !== null ? this.value : '');
        return tasks ? `${tasks.n} / ${tasks.total}` : '';
      }
      return '';
    },
  },

  mounted() {
    if (this.useTextarea) {
      this.resizeTextarea();
    }
    this.$nextTick(preventPastingStyles);

    // On a mobile device, these events allow communicating with the fullscreen-editor
    globalEventDispatcher.register(`${ShizenGlobalEvent.FS_EDITOR_START_EDIT}.${this.uuid}`, this.editionStarted);
    globalEventDispatcher.register(`${ShizenGlobalEvent.FS_EDITOR_HIDE}.${this.uuid}`, ({ value }) => {
      this.$emit('input', value, true);
      this.editing = false;
    });

    if (this.focusOnMount) {
      this.doFocus();
    }
  },

  updated() {
    this.$emit('updated');
  },

  beforeDestroy() {
    globalEventDispatcher.unregister(`${ShizenGlobalEvent.FS_EDITOR_START_EDIT}.${this.uuid}`, this.editionStarted);
    globalEventDispatcher.unregister(`${ShizenGlobalEvent.FS_EDITOR_HIDE}.${this.uuid}`);
  },

  methods: {
    doFocus() {
      // Set the focus in the field unless the edition is overridden by the fullscreen editor
      if (!this.requiresTopScreenEditor() || this.isFsEditor) {
        if (this.useTiptapEditor && this.$refs.textfield && this.$refs.textfield.doFocus) {
          this.$refs.textfield.doFocus();
        } else {
          this.$refs.area.focus();
          this.$refs.area.click();
          if (!this.useTextarea) {
            setCursorAtEnd(this.$refs.area);
          }
        }
      } else {
        if (!this.editing) {
          this.edit();
        }
        this.$nextTick(() => {
          globalEventDispatcher.dispatch(ShizenGlobalEvent.FS_EDITOR_DO_FOCUS);
        });
      }
    },
    focusing() {
      setCursorAtEnd(this.$refs.area);
      this.$emit('focus');
    },
    bInput(e) {
      if (typeof e.forceValue !== 'undefined') {
        this.value = e.forceValue;
      }
    },
    edit(evt) {
      if (evt) {
        evt.stopPropagation();
      }
      if (!this.editing) {
        // Check if editing this field requires using the fullscreen-editor
        if (!this.isFsEditor && this.requiresTopScreenEditor()) {
          globalEventDispatcher.dispatch(ShizenGlobalEvent.FS_EDITOR_SHOW, { editorOptions: this });
          return;
        }
        this.editionStarted();
        this.editing = true;
        if (this.$refs.area) {
          this.$refs.area.innerHTML = this.sanitizedValue;
        }
        if (!this.useTiptapEditor) {
          // When using contenteditable, put the cursor inside the field
          // When using tiptap, focus is done naturally on click
          this.doFocus();
        }
        setTimeout(() => {
          $(document).on('mousedown', this.clickOutside);
          $(document).on('touchstart', this.clickOutside);
          $(document).on('mouseup', this.onClickEnd);
          $(document).on('touchend', this.onClickEnd);
        }, 500);
        detectEscapeKey(this.endEdit);
      }
    },
    /**
     * Catch mousedown event instead of waiting for blur event in order to handle keywords properly :
     * The last word typed in the keyword must be converted as a keyword before the blur event
     */
    clickOutside(e) {
      if (this.preventClickOutside) {
        return;
      }
      const $target = $(e.target);
      // Clicking outside the field AND the autocomplete options
      if (!$target.closest(this.$refs.wrapper).length && !$target.closest('.ui-autocomplete').length) {
        // Make the last keyword as a removable label
        if (this.keywords) {
          autocompleteArea({ target: this.$refs.area, keyCode: 188, key: ' ', noSearch: true }, this);
        }
        this.endEditOnClickEnd = true;
        if (!this.useTextarea && !this.useTiptapEditor) {
          // If clicking a button to save or change page,
          // the value must be saved before the click event
          const val = decodeHTML(removeTrailingBreak(this.$refs.area.innerHTML));
          this.$emit('input', val, false);
        }
      }
    },
    /**
     * If clicking outside, call endEdit on mouseup after a delay.
     * If no delay, click will go through FS Editor (on tablet, FullScreen).
     */
    onClickEnd() {
      if (this.endEditOnClickEnd) {
        this.endEditOnClickEnd = false;
        setTimeout(() => {
          this.endEdit(false);
        }, 100);
        $(document).off('mousedown', this.clickOutside);
        $(document).off('touchstart', this.clickOutside);
        $(document).off('mouseup', this.onClickEnd);
        $(document).off('touchend', this.onClickEnd);
      }
    },
    editionStarted() {
      this.editing = true;
      this.$emit('startEdit');
    },
    endEdit(useEnterKey) {
      if (this.editing) {
        if (this.useTextarea && this.$refs.area) {
          if (!this.textareaSaved) {
            // Textarea is saved automatically after a delay
            // The user shouldn't lose focus on auto save
            this.textareaSaved = true;
            // Escape HTML chars
            this.$emit('input', this.$refs.area.value, !!useEnterKey);
          }
        } else {
          // Using classic div[contenteditable]
          this.editing = false;
          if (useEnterKey && this.$refs.area) {
            this.$emit('input', decodeHTML(removeTrailingBreak(this.$refs.area.innerHTML)), true);
            this.$refs.area.blur();
            $(document).off('mousedown', this.clickOutside);
            $(document).off('touchstart', this.clickOutside);
            $(document).off('mouseup', this.onClickEnd);
            $(document).off('touchend', this.onClickEnd);
          }
        }
      }
    },
    /** Blur event on textarea */
    textareaBlur() {
      this.endEdit();
      this.editing = false;
      this.$emit('blur');
    },
    inputChanged(evt) {
      $('.textarea br:not(:last-child)').remove();
      // Prevent enter key press
      if ((evt.keyCode === 13 && !this.useTextarea) || (evt.keyCode === 40 && this.autocompleteOpened)) {
        evt.preventDefault();
        return false;
      }
      // Prevent triggering autocomplete when using arrows or enter
      if (this.autocomplete && this.noEventKeyCodes.indexOf(evt.keyCode) === -1 && !isMobile) {
        autocompleteArea(evt, this);
      }
      return true;
    },
    detectEnter(evt) {
      this.$emit('keyup', evt);
      // Validate if enter pressed unless enter was used to validate an autocomplete item
      if (!this.preventNext) {
        if (evt.keyCode === 13) {
          if (this.keywords) {
            autocompleteArea({ target: this.$refs.area, keyCode: 188, noSearch: true }, this);
          }
          setTimeout(() => {
            this.endEdit(true);
          }, 100);
          return false;
        }
      } else {
        this.preventNext = false;
      }
      if (evt.keyCode !== 13 && isMobile) {
        autocompleteArea(evt, this);
      }
      return true;
    },
    /** Triggered on input on textarea if useTextarea */
    textareaEdited(e) {
      this.textareaSaved = false;
      clearTimeout(this.editTimeout);
      this.editTimeout = setTimeout(this.endEdit, this.textareaEditionTimeout);
      this.resizeTextarea(e);
    },
    resizeTextarea(e) {
      textareaAutoSize(e || { target: this.$refs.area });
      this.$emit('textareaAutoSize');
    },
    /** Resize event triggered on textarea */
    onResize() {
      this.$emit('textareaResize');
    },
    scrollToTop() {
      this.$refs.area.scrollTop = 0;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.editable-label {
  user-select: text;

  &__text-field {
    margin-top: 0.4em;
  }

  &__tasks {
    margin-left: 1em;
  }

  * {
    user-select: text;
  }

  .textarea {
    box-sizing: border-box;
    min-height: 1.3em;
    // Outline done using $color-shizen-blue-btn-shadow
    outline: none;
  }

  .editable-label-text {
    overflow-wrap: break-word;
  }
}

</style>
